// One schema-driven control for a ParamField (posterTools/types.ts), shared
// by the Smart Objects panel and the list-item modal. Handles every type
// except "list" (the callers dispatch that to SmartObjectListField to avoid
// a circular import).
//
// type "json" is kept ONLY as a legacy fallback — no shipped recipe should
// use it; educators never see raw JSON on the normal path.
import React from 'react';
import FontPicker from './toolbar/FontPicker';
import SmartObjectFillField from './SmartObjectFillField';
import { PaintField, solidHexFrom } from './primitives/PaintPicker';
import { safeHexInput } from './SmartObjectFieldUtils';

export default function SmartObjectFieldControl({ field, value, onChange }) {
  switch (field.type) {
    case 'multiline':
      return (
        <textarea
          className="cce-so-input cce-so-textarea"
          rows={3}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number': {
      // Slider + number box side by side (owner 2026-07-23: "everywhere we have
      // to insert numbers needs a slider in addition to the small arrows"). The
      // BOX stays the source of truth — its native min/max/step drive the
      // spinner arrows and typing MAY exceed the slider's range; the slider only
      // clamps its own thumb for display. Both emit the same string the drafts
      // already expect (unchanged onChange value semantics).
      const curNum = Number.parseFloat(value);
      const hasNum = Number.isFinite(curNum);
      const rMin = Number.isFinite(field.min) ? field.min : 0;
      // Range law: honor field.min/max when present. When field.max is ABSENT the
      // upper bound is LIVE-ADAPTIVE — max(2×current value, floor 100) — and it is
      // recomputed from `value` on every render, so committing a bigger number
      // (or dragging near the top) hands the track more headroom the next time it
      // opens. The box can still go past it; the slider just re-scales.
      const rMax = Number.isFinite(field.max)
        ? field.max
        : Math.max((hasNum ? curNum : 0) * 2, 100);
      const rStep = Number.isFinite(field.step) ? field.step : ((rMax - rMin) <= 5 ? 0.1 : 1);
      const sliderVal = hasNum ? Math.min(rMax, Math.max(rMin, curNum)) : rMin;
      return (
        <div className="cce-so-slider-row">
          <input
            type="range"
            className="cce-so-range"
            min={rMin}
            max={rMax}
            step={rStep}
            value={sliderVal}
            aria-label={field.label}
            onChange={(e) => onChange(e.target.value)}
          />
          <input
            type="number"
            className="cce-so-input cce-so-slider-num"
            value={value ?? ''}
            min={field.min}
            max={field.max}
            step={field.step}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }
    case 'color': {
      // Plain hex param (consumed as a raw color string — no resolveFill), so
      // the house PaintField runs solid-only + opaque: it emits '#rrggbb',
      // byte-identical to the retired <input type=color>. Gradient/texture are
      // withheld honestly — str() consumers would choke on a FillValue object.
      const hex = typeof value === 'string' ? value : solidHexFrom(value, '#000000');
      return (
        <div className="cce-so-paint">
          <PaintField
            label={field.label}
            dataField={field.key}
            value={hex}
            allow={['solid']}
            opaque
            chipClassName="cce-so-paint-chip"
            onChange={(next) => onChange(typeof next === 'string' ? next : solidHexFrom(next, '#000000'))}
          />
          <span className="cce-so-paint-sum">{safeHexInput(hex, '#000000')}</span>
        </div>
      );
    }
    case 'select': {
      // Options MAY carry `group` (types.ts): consecutive same-group options
      // render inside an <optgroup>; ungrouped options stay bare. Contiguous-run
      // grouping preserves the schema's option order exactly (e.g. an element
      // picker's Period 1…7 sections + a trailing bare "Custom"). A plain
      // ungrouped select renders byte-identically to the previous .map().
      const opts = field.options || [];
      const nodes = [];
      let run = null; // { group, items: [] }
      const flushRun = () => {
        if (!run) return;
        if (run.group) {
          nodes.push(<optgroup key={`og-${nodes.length}`} label={run.group}>{run.items}</optgroup>);
        } else {
          for (const it of run.items) nodes.push(it);
        }
        run = null;
      };
      opts.forEach((opt, i) => {
        const g = opt.group || '';
        if (!run || run.group !== g) { flushRun(); run = { group: g, items: [] }; }
        run.items.push(
          <option key={opt.value ?? `opt-${i}`} value={opt.value}>{opt.label}</option>,
        );
      });
      flushRun();
      return (
        <select
          className="cce-so-input"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        >
          {nodes}
        </select>
      );
    }
    case 'boolean':
      return (
        <label className="cce-so-checkbox-row">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>Enabled</span>
        </label>
      );
    case 'fill':
      return (
        <SmartObjectFillField
          value={value}
          onChange={onChange}
          label={field.label}
          dataField={field.key}
        />
      );
    case 'font':
      // Real font system: FontPicker ensures the family is registered in
      // document.fonts (ensureFont) BEFORE onSelect fires, so text measures
      // correctly when Apply regenerates. handleApply re-ensures as a belt
      // and braces for families seeded from existing params.
      return (
        <div className="cce-so-font">
          <FontPicker value={value || 'Arial'} onSelect={onChange} />
        </div>
      );
    case 'image':
      return (
        <input
          type="text"
          className="cce-so-input"
          placeholder="https://… image URL"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'json': // legacy fallback only — hidden from the normal educator path
      return (
        <textarea
          className="cce-so-input cce-so-textarea cce-so-json"
          rows={5}
          spellCheck={false}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'text':
    default:
      return (
        <input
          type="text"
          className="cce-so-input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
